import React, { useEffect, useRef, useState } from "react";
import $ from "jquery";
import { useNavigate } from "react-router-dom";
import ApiURl from "../controllers/Api";
import usericon from "../images/usericon.jpg";
import { gsap } from "gsap";

/* ============================================================
   SL GOLD THEME
   Same variables used across the website
   ============================================================ */

const PAGE_BG = "var(--user-bg, #E8D9D5)";
const MAROON = "var(--user-maroon, #2B0000)";
const GOLD = "var(--user-gold, #D4AF37)";

const CREAM = "#FBF7F2";
const SOFT_CREAM = "#F6EFEA";

const BORDER = "rgba(43,0,0,.12)";
const GOLD_BORDER = "rgba(212,175,55,.30)";
const TEXT_SOFT = "rgba(43,0,0,.62)";
const TEXT_LIGHT = "rgba(43,0,0,.44)";

/* ============================================================
   ICONS
   ============================================================ */

const UserIcon = ({ size = 17 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 21a8 8 0 0 0-16 0" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const PhoneIcon = ({ size = 17 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const MailIcon = ({ size = 17 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 7 9 6 9-6" />
  </svg>
);

const CalendarIcon = ({ size = 17 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M16 3v4M8 3v4M3 11h18" />
  </svg>
);

const LockIcon = ({ size = 15 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="4" y="10" width="16" height="11" rx="2" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
  </svg>
);

const BagIcon = ({ size = 15 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 8h12l1 13H5L6 8Z" />
    <path d="M9 8V6a3 3 0 0 1 6 0v2" />
  </svg>
);

const ArrowIcon = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 12h14" />
    <path d="m13 6 6 6-6 6" />
  </svg>
);

/* ============================================================
   PROFILE
   ============================================================ */

const ProfileSection = () => {
  const [userData, setUserData] = useState({});
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  const wrapRef = useRef(null);
  const cardRef = useRef(null);
  const headerRef = useRef(null);
  const avatarRef = useRef(null);
  const infoRef = useRef(null);

  /* ============================================================
     GET USER
     ============================================================ */

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      setLoading(false);
      return;
    }

    const clearAuthState = () => {
      localStorage.removeItem("role");
      localStorage.removeItem("token");
      localStorage.removeItem("userId");
      localStorage.removeItem("user_id");
      localStorage.removeItem("phone");
      localStorage.removeItem("cart_id");
      localStorage.removeItem("userData");

      window.dispatchEvent(
        new Event("authChanged")
      );

      navigate("/login");
    };

    $.ajax({
      url: `${ApiURl}/getUser.php`,
      method: "GET",
      data: { token },
    })
      .done((response) => {
        if (response?.success) {
          setUserData(
            response.ver_user || {}
          );
          return;
        }

        clearAuthState();
      })
      .fail((error) => {
        console.error(
          "Error fetching user data:",
          error
        );

        clearAuthState();
      })
      .always(() => {
        setLoading(false);
      });
  }, [navigate]);

  /* ============================================================
     ANIMATION
     ============================================================ */

  useEffect(() => {
    if (
      !wrapRef.current ||
      loading
    ) {
      return;
    }

    const prefersReducedMotion =
      window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

    if (prefersReducedMotion) {
      return;
    }

    const ctx = gsap.context(() => {
      gsap.fromTo(
        cardRef.current,
        {
          opacity: 0,
          y: 14,
          scale: 0.99,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.55,
          ease: "power2.out",
        }
      );

      gsap.fromTo(
        headerRef.current,
        {
          y: 10,
          opacity: 0,
        },
        {
          y: 0,
          opacity: 1,
          duration: 0.45,
          ease: "power2.out",
          delay: 0.05,
        }
      );

      gsap.fromTo(
        avatarRef.current,
        {
          scale: 0.92,
          opacity: 0,
          y: 10,
        },
        {
          scale: 1,
          opacity: 1,
          y: 0,
          duration: 0.5,
          ease: "power2.out",
          delay: 0.08,
        }
      );

      gsap.fromTo(
        infoRef.current,
        {
          opacity: 0,
          y: 10,
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.5,
          ease: "power2.out",
          delay: 0.12,
        }
      );

      gsap.fromTo(
        ".profile-field",
        {
          opacity: 0,
          y: 10,
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.45,
          ease: "power2.out",
          stagger: 0.05,
          delay: 0.16,
        }
      );
    }, wrapRef);

    return () => ctx.revert();
  }, [loading]);

  /* ============================================================
     DATA
     ============================================================ */

  const fields = [
    {
      label: "Username",
      value: userData.username || "-",
      icon: <UserIcon />,
    },
    {
      label: "First Name",
      value: userData.firstname || "-",
      icon: <UserIcon />,
    },
    {
      label: "Last Name",
      value: userData.lastname || "-",
      icon: <UserIcon />,
    },
    {
      label: "Phone Number",
      value: userData.phone
        ? `+91 ${userData.phone}`
        : "-",
      icon: <PhoneIcon />,
    },
    {
      label: "Email",
      value: userData.email || "-",
      icon: <MailIcon />,
    },
    {
      label: "Member Since",
      value: userData.created_at || "-",
      icon: <CalendarIcon />,
    },
  ];

  const fullName = `${
    userData?.firstname || "User"
  } ${userData?.lastname || ""}`.trim();

  /* ============================================================
     LOADING
     ============================================================ */

  if (loading) {
    return (
      <section
        className="
          flex
          min-h-[75vh]
          w-full
          items-center
          justify-center

          px-3
          py-10

          sm:px-6
          sm:py-14
        "
        style={{
          backgroundColor: PAGE_BG,
        }}
      >
        <div className="w-full max-w-[1050px]">

          <div
            className="
              overflow-hidden
              rounded-[22px]
              border
            "
            style={{
              backgroundColor: CREAM,
              borderColor: GOLD_BORDER,
            }}
          >
            {/* HEADER SKELETON */}

            <div
              className="
                border-b
                px-5
                py-5

                sm:px-7
                sm:py-6
              "
              style={{
                borderColor: BORDER,
              }}
            >
              <div
                className="
                  h-3
                  w-20
                  animate-pulse
                  rounded-full
                "
                style={{
                  backgroundColor:
                    "rgba(43,0,0,.08)",
                }}
              />

              <div
                className="
                  mt-3
                  h-8
                  w-40
                  animate-pulse
                  rounded
                "
                style={{
                  backgroundColor:
                    "rgba(43,0,0,.10)",
                }}
              />

              <div
                className="
                  mt-3
                  h-3
                  w-64
                  max-w-full
                  animate-pulse
                  rounded
                "
                style={{
                  backgroundColor:
                    "rgba(43,0,0,.06)",
                }}
              />
            </div>

            <div className="p-5 sm:p-7">

              <div
                className="
                  flex
                  flex-col
                  gap-5

                  sm:flex-row
                  sm:items-center
                "
              >
                <div
                  className="
                    h-[82px]
                    w-[82px]
                    animate-pulse
                    rounded-full

                    sm:h-[96px]
                    sm:w-[96px]
                  "
                  style={{
                    backgroundColor:
                      "rgba(212,175,55,.13)",
                  }}
                />

                <div className="flex-1">

                  <div
                    className="
                      h-6
                      w-52
                      max-w-full
                      animate-pulse
                      rounded
                    "
                    style={{
                      backgroundColor:
                        "rgba(43,0,0,.10)",
                    }}
                  />

                  <div
                    className="
                      mt-2
                      h-3
                      w-64
                      max-w-full
                      animate-pulse
                      rounded
                    "
                    style={{
                      backgroundColor:
                        "rgba(43,0,0,.06)",
                    }}
                  />

                  <div
                    className="
                      mt-5
                      flex
                      gap-2
                    "
                  >
                    <div
                      className="
                        h-9
                        w-36
                        animate-pulse
                        rounded-full
                      "
                      style={{
                        backgroundColor:
                          "rgba(43,0,0,.10)",
                      }}
                    />

                    <div
                      className="
                        h-9
                        w-32
                        animate-pulse
                        rounded-full
                      "
                      style={{
                        backgroundColor:
                          "rgba(212,175,55,.10)",
                      }}
                    />
                  </div>

                </div>
              </div>

              <div
                className="
                  mt-7
                  grid
                  grid-cols-1
                  gap-3

                  sm:grid-cols-2

                  lg:grid-cols-3
                "
              >
                {[...Array(6)].map(
                  (_, index) => (
                    <div
                      key={index}
                      className="
                        h-[90px]
                        animate-pulse
                        rounded-[15px]
                      "
                      style={{
                        backgroundColor:
                          SOFT_CREAM,
                      }}
                    />
                  )
                )}
              </div>

            </div>
          </div>

        </div>
      </section>
    );
  }

  /* ============================================================
     MAIN UI
     ============================================================ */

  return (
    <section
      ref={wrapRef}
      className="
        flex
        min-h-[75vh]
        w-full
        items-center
        justify-center

        px-3
        py-10

        sm:px-6
        sm:py-14

        lg:px-10
        lg:py-16
      "
      style={{
        backgroundColor: PAGE_BG,
      }}
    >
      <div
        className="
          w-full
          max-w-[1050px]
        "
      >

        {/* ======================================================
            PAGE HEADING
            ====================================================== */}

        <div
          ref={headerRef}
          className="
            mb-7
            text-center

            sm:mb-9
          "
        >
          <div
            className="
              mb-3
              flex
              items-center
              justify-center
              gap-3
            "
          >
            <span
              className="
                h-px
                w-7

                sm:w-9
              "
              style={{
                backgroundColor: MAROON,
                opacity: 0.48,
              }}
            />

            <span
              className="
                text-[8px]
                font-semibold
                uppercase
                tracking-[0.28em]

                sm:text-[9px]
              "
              style={{
                color: MAROON,
              }}
            >
              SL Gold
            </span>

            <span
              className="
                h-px
                w-7

                sm:w-9
              "
              style={{
                backgroundColor: MAROON,
                opacity: 0.48,
              }}
            />
          </div>

          <h1
            className="
              font-serif

              text-[32px]
              font-medium
              leading-[1.05]
              tracking-[-0.025em]

              sm:text-[38px]

              lg:text-[44px]
            "
            style={{
              color: MAROON,
            }}
          >
            My Profile
          </h1>

          <p
            className="
              mx-auto
              mt-2
              max-w-[500px]

              font-serif
              text-[13px]
              leading-[1.55]

              sm:text-[15px]
            "
            style={{
              color: MAROON,
              opacity: 0.62,
            }}
          >
            Your personal details and account
            information.
          </p>
        </div>

        {/* ======================================================
            MAIN PROFILE CARD
            ====================================================== */}

        <div
          ref={cardRef}
          className="
            overflow-hidden
            rounded-[20px]
            border
          "
          style={{
            backgroundColor: CREAM,
            borderColor: GOLD_BORDER,
            boxShadow:
              "0 14px 40px rgba(43,0,0,.055)",
          }}
        >

          {/* ====================================================
              PROFILE HERO
              ==================================================== */}

          <div
            className="
              relative
              overflow-hidden

              px-5
              py-6

              sm:px-7
              sm:py-7
            "
            style={{
              backgroundColor: MAROON,
            }}
          >

            {/* SUBTLE GOLD CIRCLE */}

            <div
              className="
                pointer-events-none
                absolute
                -right-16
                -top-20

                h-[190px]
                w-[190px]
                rounded-full
                border
              "
              style={{
                borderColor:
                  "rgba(212,175,55,.13)",
              }}
            />

            <div
              className="
                pointer-events-none
                absolute
                -right-6
                -top-10

                h-[110px]
                w-[110px]
                rounded-full
                border
              "
              style={{
                borderColor:
                  "rgba(212,175,55,.10)",
              }}
            />

            <div
              className="
                relative
                z-10

                flex
                flex-col
                gap-5

                sm:flex-row
                sm:items-center
              "
            >

              {/* AVATAR */}

              <div
                ref={avatarRef}
                className="shrink-0"
              >
                <div
                  className="
                    rounded-full
                    border
                    p-[3px]
                  "
                  style={{
                    borderColor: GOLD,
                  }}
                >
                  <div
                    className="
                      rounded-full
                      border-[3px]
                      p-[2px]
                    "
                    style={{
                      borderColor: MAROON,
                      backgroundColor: CREAM,
                    }}
                  >
                    <img
                      src={usericon}
                      alt="Profile"
                      className="
                        h-[76px]
                        w-[76px]
                        rounded-full
                        object-cover

                        sm:h-[90px]
                        sm:w-[90px]
                      "
                    />
                  </div>
                </div>
              </div>

              {/* USER */}

              <div
                ref={infoRef}
                className="
                  min-w-0
                  flex-1
                "
              >

                <p
                  className="
                    text-[8px]
                    font-semibold
                    uppercase
                    tracking-[.2em]
                  "
                  style={{
                    color: GOLD,
                  }}
                >
                  SL Gold Member
                </p>

                <h2
                  className="
                    mt-1
                    break-words
                    font-serif

                    text-[25px]
                    font-medium
                    leading-tight

                    text-white

                    sm:text-[29px]
                  "
                >
                  {fullName}
                </h2>

                <div
                  className="
                    mt-1.5
                    flex
                    items-center
                    gap-2
                  "
                  style={{
                    color:
                      "rgba(255,255,255,.62)",
                  }}
                >
                  <MailIcon size={13} />

                  <span
                    className="
                      min-w-0
                      break-all
                      text-[10px]

                      sm:text-[11px]
                    "
                  >
                    {userData.email || "-"}
                  </span>
                </div>

                {/* BUTTONS */}

                <div
                  className="
                    mt-5
                    flex
                    flex-wrap
                    gap-2
                  "
                >

                  <button
                    type="button"
                    onClick={() =>
                      navigate(
                        "/ForgetPassword"
                      )
                    }
                    className="
                      inline-flex
                      items-center
                      justify-center
                      gap-2

                      rounded-full
                      border

                      px-4
                      py-2.5

                      text-[9px]
                      font-semibold
                      uppercase
                      tracking-[.1em]

                      transition-opacity
                      duration-200

                      hover:opacity-90

                      sm:px-5
                    "
                    style={{
                      color: MAROON,
                      backgroundColor: GOLD,
                      borderColor: GOLD,
                    }}
                  >
                    <LockIcon />

                    Change Password
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      navigate(
                        "/order-section"
                      )
                    }
                    className="
                      inline-flex
                      items-center
                      justify-center
                      gap-2

                      rounded-full
                      border

                      px-4
                      py-2.5

                      text-[9px]
                      font-semibold
                      uppercase
                      tracking-[.1em]

                      transition-colors
                      duration-200

                      sm:px-5
                    "
                    style={{
                      color: GOLD,
                      borderColor:
                        "rgba(212,175,55,.38)",
                      backgroundColor:
                        "rgba(255,255,255,.035)",
                    }}
                  >
                    <BagIcon />

                    View Orders

                    <ArrowIcon size={12} />
                  </button>

                </div>

              </div>

            </div>
          </div>

          {/* ====================================================
              PERSONAL INFORMATION
              ==================================================== */}

          <div
            className="
              px-4
              py-5

              sm:px-6
              sm:py-6

              lg:px-7
              lg:py-7
            "
          >

            {/* SECTION TITLE */}

            <div
              className="
                flex
                items-end
                justify-between
                gap-4

                border-b
                pb-4
              "
              style={{
                borderColor: BORDER,
              }}
            >

              <div>

                <p
                  className="
                    text-[8px]
                    font-semibold
                    uppercase
                    tracking-[.2em]
                  "
                  style={{
                    color: GOLD,
                  }}
                >
                  Account
                </p>

                <h3
                  className="
                    mt-1
                    font-serif

                    text-[20px]
                    font-medium

                    sm:text-[22px]
                  "
                  style={{
                    color: MAROON,
                  }}
                >
                  Personal Information
                </h3>

              </div>

              <div
                className="
                  hidden
                  h-9
                  w-9
                  items-center
                  justify-center

                  rounded-full

                  sm:flex
                "
                style={{
                  color: GOLD,
                  backgroundColor: MAROON,
                }}
              >
                <UserIcon size={16} />
              </div>

            </div>

            {/* ==================================================
                FIELDS
                ================================================== */}

            <div
              className="
                mt-4
                grid
                grid-cols-1
                gap-2.5

                sm:grid-cols-2
                sm:gap-3

                lg:grid-cols-3
              "
            >
              {fields.map((field, index) => (
                <div
                  key={index}
                  className="
                    profile-field

                    group

                    rounded-[14px]
                    border

                    p-3.5

                    sm:p-4
                  "
                  style={{
                    backgroundColor:
                      SOFT_CREAM,
                    borderColor: BORDER,
                  }}
                >

                  <div
                    className="
                      flex
                      items-start
                      gap-3
                    "
                  >

                    {/* ICON */}

                    <div
                      className="
                        flex
                        h-8
                        w-8
                        shrink-0
                        items-center
                        justify-center

                        rounded-full
                        border
                      "
                      style={{
                        color: GOLD,
                        backgroundColor:
                          MAROON,
                        borderColor:
                          GOLD_BORDER,
                      }}
                    >
                      {React.cloneElement(
                        field.icon,
                        {
                          size: 13,
                        }
                      )}
                    </div>

                    {/* TEXT */}

                    <div
                      className="
                        min-w-0
                        flex-1
                      "
                    >
                      <p
                        className="
                          text-[8px]
                          font-semibold
                          uppercase
                          tracking-[.14em]
                        "
                        style={{
                          color:
                            TEXT_LIGHT,
                        }}
                      >
                        {field.label}
                      </p>

                      <p
                        className="
                          mt-1
                          break-words

                          font-serif
                          text-[14px]
                          font-medium
                          leading-[1.35]

                          sm:text-[15px]
                        "
                        style={{
                          color: MAROON,
                        }}
                      >
                        {field.value}
                      </p>
                    </div>

                  </div>

                </div>
              ))}
            </div>

            {/* ==================================================
                SECURITY / OTP NOTE
                ================================================== */}

            <div
              className="
                mt-5

                flex
                items-start
                gap-3

                rounded-[14px]
                border

                p-3.5

                sm:p-4
              "
              style={{
                backgroundColor:
                  "rgba(212,175,55,.07)",
                borderColor:
                  GOLD_BORDER,
              }}
            >

              <div
                className="
                  flex
                  h-8
                  w-8
                  shrink-0
                  items-center
                  justify-center

                  rounded-full
                "
                style={{
                  backgroundColor:
                    MAROON,
                  color: GOLD,
                }}
              >
                <PhoneIcon size={13} />
              </div>

              <div>

                <p
                  className="
                    text-[8px]
                    font-semibold
                    uppercase
                    tracking-[.16em]
                  "
                  style={{
                    color: GOLD,
                  }}
                >
                  Account Tip
                </p>

                <p
                  className="
                    mt-1
                    text-[10px]
                    leading-[1.55]

                    sm:text-[11px]
                  "
                  style={{
                    color: TEXT_SOFT,
                  }}
                >
                  Keep your phone number
                  updated to receive OTP and
                  delivery updates.
                </p>

              </div>

            </div>

          </div>

          {/* ====================================================
              BOTTOM GOLD DETAIL
              ==================================================== */}

          <div
            className="
              h-[2px]
              w-full
            "
            style={{
              background:
                "linear-gradient(90deg, transparent, rgba(212,175,55,.70), transparent)",
            }}
          />

        </div>

      </div>
    </section>
  );
};

export default ProfileSection;